<i18n>
    [
        "global__retail",
        "browse__prepurchase--tilePriceIntro",
        "global__off",
    ]
</i18n>

<template>
    <div class="c-catalog-tile-details">
        <div
            class="c-catalog-tile-details__fade c-catalog-tile-details__info-section"
            :class="{
                'c-catalog-tile-details__fade--in-view': hasEnteredViewport,
            }"
        >
            <BaseLink
                v-if="!hideInfo"
                :to="productRoute"
                class="c-catalog-tile-details__info-section__link u-display--block"
                tabindex="-1"
                @click="emitClick"
            >
                <div
                    :class="{
                        'c-catalog-tile-details__info-container': true,
                        'c-catalog-tile-details__info-container--light': lightInfoContainer,
                    }"
                >
                    <p
                        class="c-catalog-tile-details__display-name o-text--heading-6 u-text--black"
                    >
                        {{ product.displayName }}
                    </p>
                    <div
                        v-if="isPrePurchase"
                        class="c-catalog-tile-details__product-pre-purchase-label o-text--caption u-text--gray"
                    >
                        <div
                            class="
                                c-catalog-tile-details__product-pre-purchase-label-brand
                                o-text--caption
                                u-text--gray
                            "
                        >
                            {{ brandName }}
                        </div>
                        <template v-if="hasEnteredViewport">
                            <div class="o-text--caption u-text--black">
                                {{ t('browse__prepurchase--tilePriceIntro') }}
                                <span class="c-catalog-tile-details__strike-through">
                                    {{ prePurchaseListPrice }}
                                </span>
                                {{ prePurchaseSalePrice }}
                                <span v-if="!hideDiscountPercentage">
                                    {{ discountPercentage }}
                                </span>
                            </div>
                        </template>
                    </div>
                    <div
                        v-else
                        class="c-catalog-tile-details__product-label o-text--caption u-text--gray u-text--center"
                    >
                        <div
                            :class="{
                                'c-catalog-tile-details__product-label-brand':
                                    hidePrice && hasEnteredViewport,
                                'c-catalog-tile-details__product-label-brand-mobile':
                                    !(hidePrice && hasEnteredViewport),
                            }"
                            class="o-text--caption u-text--gray"
                        >
                            {{ brandName }}
                        </div>
                        <template v-if="hidePrice && hasEnteredViewport">
                            <div class="u-text--medium-gray">
                                |
                            </div>
                            <div
                                class="c-catalog-tile-details__product-label-price o-text--caption u-text--gray"
                            >
                                {{ `${t('global__retail')} ${retailPrice}` }}
                            </div>
                        </template>
                    </div>
                </div>
            </BaseLink>
            <slot></slot>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';
import { useFilters } from '~coreModules/core/js/custom-filters';
import { useMediaQueries } from '~coreModules/core/js/composables/useMediaQueries';

import { getFilteredVendorBrands } from '~coreRoutes/product/js/product-utils';

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    productRoute: {
        type: Object,
        required: true,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
    hideInfo: {
        type: Boolean,
        default: false,
    },
    productTrayId: {
        type: String,
        default: null,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    catalogResponseId: {
        type: String,
        default: null,
    },
    showShopTheLookBanner: {
        type: Boolean,
        default: false,
    },
    productListName: {
        type: String,
        default: '',
    },
    productListId: {
        type: String,
        default: '',
    },
    isPrePurchase: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['click']);

const filters = useFilters();
const { t } = useI18n();
const mediaQueries = useMediaQueries();

function emitClick() {
    emit('click');
}

const prePurchaseSalePrice = computed(() => (
    filters.i18nFormatDollarsNoCents(props.product.prePurchase?.salePrice)
));
const discountPercentage = computed(() => `(${props.product?.prePurchase?.discountPercent}% ${t('global__off')})`);
const prePurchaseListPrice = computed(() => (
    filters.i18nFormatDollarsNoCents(props.product.prePurchase?.listPrice)
));

const hideDiscountPercentage = computed(() => mediaQueries.isExtraSmall);

const retailPrice = computed(() => filters.i18nFormatDollarsNoCents(props.product.msrpValue));
const brandName = computed(() => getFilteredVendorBrands(props.product?.vendorBrands || []));
const hidePrice = computed(() => !mediaQueries.isSmallish);

</script>

<style lang="scss">
.c-catalog-tile-details {
    &__fade {
        opacity: 0;
        transition: opacity .5s ease;

        &--in-view {
            opacity: 1;
        }
    }

    &__info-section {
        position: relative;

        &__link {
            outline-offset: -4px;
        }
    }

    &__info-container {
        @include ellipsize;

        background-color: $nu-secondary;
        padding: $nu-spacer-1pt5;
        width: 100%;
        min-height: 40px;
        text-align: center;

        &--light {
            background-color: $nu-white;
        }

        @include breakpoint(medium) {
            padding: $nu-spacer-2;
        }
    }

    &__display-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: $nu-spacer-1;
    }

    &__product-pre-purchase-label {
        display: flex;
        width: 100%;
        align-items: center;
        flex-direction: column;

        &-brand {
            margin-bottom: $nu-spacer-1;
        }
    }

    &__product-label {
        display: flex;
        width: 100%;
        justify-content: center;
    }

    &__product-label-brand {
        @include ellipsize;
        text-align: right;
        padding-right: $nu-spacer-1;
    }

    &__product-label-brand-mobile {
        @include ellipsize;
    }

    &__product-label-price {
        text-align: left;
        padding-left: $nu-spacer-1;
    }

    &__strike-through {
        text-decoration: line-through;
    }
}
</style>
